import { useState } from "react";
import { HttpStatus } from "pages/Core";
import notistack from "pages/Core/Notistack";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import useHasPermission from "hooks/useHasPermission";
import { handleKeyDown } from "Common/Modal/ModalTriggerButton";
import UpdateSubCategory from "./UpdateSubCategory";
import { DeleteSubCategory, getSubCategoryById } from "../services/SubCategoryservises";
import { SubCategoryDataModel } from "../Model/SubCategoryModal";

interface CategoryProp {
    selectedCategoryId: string;
    selectedCategoryName: string;
    onDeleteSuccess: () => void;
}

const ActionSubCategory: React.FC<CategoryProp> = ({
    selectedCategoryId,
    selectedCategoryName,
    onDeleteSuccess,
}) => {
    const hasPermission = useHasPermission();
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [subCategoryDetails, setSubCategoryDetails] = useState<SubCategoryDataModel | null>(null);
    const [showSubCategoryDeleteModal, setShowSubCategoryDeleteModal] = useState(false);
    const [showUpdateSubCategorySourceModal, setShowUpdateSubCategoryModal] =
        useState(false);

    const handleDeleteCategory = (id: number) => {
        setSelectedId(id);
        setShowSubCategoryDeleteModal(true);
    };

    const onActionSuccess = () => {
        onDeleteSuccess();
    };

    const confirmDeleteCategory = async () => {
        if (selectedId === null) return;
        try {
            setLoading(true);
            const response = await DeleteSubCategory(selectedId);
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                setShowSubCategoryDeleteModal(false);
                onDeleteSuccess();
            }
        } catch (error) {
            console.error("Error deleting Category:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateCategoryById = async (storeData: any) => {
        try {
            setLoading(true);
            const response = await getSubCategoryById(storeData?.id);
            if (response.status === HttpStatus.Success) {
                setSubCategoryDetails(response.data);
                const data = response.data;
                setSubCategoryDetails({
                    id: data.id,
                    name: data.name,
                    categoryId: Number(data.categoryId)
                });
                setShowUpdateSubCategoryModal(true);
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error) {
            console.error("Failed to fetch SubCategory:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowUpdateSubCategoryModal(false);
    };

    return (
        <>

            {hasPermission("SubCategory.Delete") && (
                <button
                    onKeyDown={handleKeyDown}
                    className="btn btn-sm action-btn delete-btn"
                    title="Delete SubCategory"
                    onClick={() => handleDeleteCategory(Number(selectedCategoryId))}
                >
                    <FontAwesomeIcon icon={faTrash} className="ml-2" />
                </button>
            )}

            {hasPermission("SubCategory.Write") && (
                <button
                    onKeyDown={handleKeyDown}
                    className="btn btn-sm action-btn edit-btn"
                    title="Edit SubCategory"
                    onClick={() => handleUpdateCategoryById({ id: selectedCategoryId })}
                >
                    <FontAwesomeIcon icon={faPenToSquare} className="ml-2" />
                </button>
            )}

            <Modal
                show={showSubCategoryDeleteModal}
                onHide={() => setShowSubCategoryDeleteModal(false)}
                backdrop="static"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-break">
                    Are you sure you want to delete <strong>{selectedCategoryName}</strong> SubCategory?
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="btn add-btn"
                        onClick={() => setShowSubCategoryDeleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="btn submit-btn"
                        onClick={confirmDeleteCategory}
                        disabled={loading}
                    >
                        {loading ? "Deleting..." : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showUpdateSubCategorySourceModal}
                onHide={() => setShowUpdateSubCategoryModal(false)}
                className="fade"
                dialogClassName="modal-dialog-centered"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Update SubCategory </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UpdateSubCategory
                        subCategoryDetails={subCategoryDetails}
                        closeModal={handleCloseModal}
                        onUpdateSuccess={onActionSuccess}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ActionSubCategory;
